import { Add, TrashCan } from '@carbon/icons-react';
import { Button, InlineNotification, Modal } from '@carbon/react';
import {
  asProblem,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  RowAction,
  StatusTag,
  useAdmin,
  useFormat,
  useLocalPages,
} from '@pos/admin-ui';
import { fold, useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { AccountDialog } from './AccountDialog';
import {
  legalEntitiesKey,
  listAllPages,
  legalEntitiesApi,
  brandsKey,
  providerApi,
  scopeApis,
  type AccountHealth,
  type ProviderAccount,
  type ProviderCapability,
} from './deliveryApi';
import './delivery.scss';

/** What differs between the capabilities' account pages: who may change them and the words. */
const AREAS = {
  shipping: {
    manage: 'ho.config.delivery_services.manage',
    title: 'delivery.providers.title',
    intro: 'delivery.providers.intro',
    empty: 'delivery.providers.empty',
  },
  einvoice: {
    manage: 'ho.einvoice.manage',
    title: 'einvoice.providers.title',
    intro: 'einvoice.providers.intro',
    empty: 'einvoice.providers.empty',
  },
  messaging: {
    manage: 'ho.online.messaging.manage',
    title: 'messaging.providers.title',
    intro: 'messaging.providers.intro',
    empty: 'messaging.providers.empty',
  },
  payment: {
    manage: 'ho.config.payment_methods.manage',
    title: 'onlinePayments.providers.title',
    intro: 'onlinePayments.providers.intro',
    empty: 'onlinePayments.providers.empty',
  },
} as const satisfies Record<ProviderCapability, { manage: string; title: I18nKey; intro: I18nKey; empty: I18nKey }>;

/** An account's health (ADR 0113) as the shared StatusTag colours it. */
const HEALTH_TONE: Record<string, string> = { healthy: 'active', failing: 'failed', needs_attention: 'declined', idle: 'inactive' };

/** The delivery provider accounts (ADR 0025, FR-DLV-03). */
export function DeliveryProvidersPage() {
  return <ProviderAccountsPage capability="shipping" />;
}

/**
 * A capability's provider accounts (ADR 0025, ADR 0048, ADR 0072): which provider, which environment, its settings, and whether
 * its credentials are set. Credentials are entered here but never shown again. An e-invoice account belongs to a legal entity;
 * a messaging account to a brand, or to all of them.
 */
export function ProviderAccountsPage({ capability }: { capability: ProviderCapability }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const area = AREAS[capability];
  const canEdit = hasPermission(session, area.manage);
  const queryClient = useQueryClient();
  const format = useFormat();
  const { providersKey, accountsKey, healthKey, listProviders, listAccounts, listHealth, deleteAccount } = useMemo(
    () => providerApi(capability),
    [capability],
  );
  const providers = useQuery({ queryKey: providersKey, queryFn: listProviders });
  const accounts = useQuery({ queryKey: accountsKey, queryFn: listAccounts });
  // How each account fared with its provider in the last hour (ADR 0113); refreshed every minute while the page is open.
  const health = useQuery({ queryKey: healthKey, queryFn: listHealth, refetchInterval: 60_000 });
  const healthOf = (id: string): AccountHealth | undefined => health.data?.find((h) => h.accountId === id);
  const attention = (accounts.data ?? []).filter((a) => healthOf(a.id)?.status === 'needs_attention');
  // An e-invoice account issues for a company, and payments go to one (ADR 0048, ADR 0074).
  const byEntity = capability === 'einvoice' || capability === 'payment';
  const entities = useQuery({
    queryKey: legalEntitiesKey,
    queryFn: () => listAllPages(legalEntitiesApi),
    enabled: byEntity,
  });
  const byBrand = capability === 'messaging';
  const brands = useQuery({ queryKey: brandsKey, queryFn: () => listAllPages(scopeApis.brands), enabled: byBrand });
  const brandName = (id: string | null | undefined) =>
    id ? (brands.data?.find((b) => b.id === id)?.name ?? '—') : t('messaging.providers.allBrands');
  const entityName = (id: string | null | undefined) => {
    const entity = id ? entities.data?.find((e) => e.id === id) : undefined;
    return entity ? `${entity.legalName} (${entity.taxCode})` : '—';
  };
  const [editing, setEditing] = useState<ProviderAccount | 'new'>();
  const [deleting, setDeleting] = useState<ProviderAccount>();
  // The accounts API returns every account in one call, so the table searches and pages them here.
  const [search, setSearch] = useState('');
  const remove = useMutation({
    mutationFn: (account: ProviderAccount) => deleteAccount(account.id),
    onSuccess: async () => {
      setDeleting(undefined);
      await queryClient.invalidateQueries({ queryKey: accountsKey });
    },
  });

  const providerOf = (id: string) => providers.data?.find((p) => p.id === id);
  const secretsNeeded = (account: ProviderAccount) =>
    providerOf(account.providerId)?.settings.filter((s) => s.isSecret) ?? [];

  const query = fold(search.trim());
  const pages = useLocalPages((accounts.data ?? []).filter((a) => !query || fold(`${a.name} ${a.providerName}`).includes(query)));
  const failure = providers.error ?? accounts.error;
  // As on every list screen: search and Create in the table's toolbar, a row opens for editing, Delete on the row.
  const createButton = canEdit ? (
    <Button renderIcon={Add} disabled={!providers.data} onClick={() => setEditing('new')}>
      {t('admin.create')}
    </Button>
  ) : undefined;

  return (
    <section className="pos-admin-page">
      <PageHeader title={t(area.title)} description={t(area.intro)} />
      {attention.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t('providers.health.attentionTitle')}
          subtitle={attention.map((a) => `${a.name} (${healthOf(a.id)?.lastErrorCode ?? '—'})`).join(', ')}
        />
      )}
      <ListTable<ProviderAccount>
        ariaLabel={t(area.title)}
        loading={accounts.isLoading}
        error={failure ? problemMessage(t, asProblem(failure)) : undefined}
        onRetry={() => {
          void providers.refetch();
          void accounts.refetch();
        }}
        rows={pages.rows}
        search={{
          label: t('admin.search'),
          onChange: (value) => {
            setSearch(value);
            pages.reset();
          },
        }}
        toolbarActions={createButton}
        onRowClick={canEdit ? (a) => setEditing(a) : undefined}
        columns={[
          { key: 'name', header: t('delivery.providers.columns.name') },
          { key: 'providerName', header: t('delivery.providers.columns.provider') },
          ...(byBrand
            ? [{ key: 'brandId', header: t('messaging.providers.brand'), render: (a: ProviderAccount) => brandName(a.brandId) }]
            : []),
          ...(byEntity
            ? [
                {
                  key: 'legalEntityId',
                  header: t('md.fields.legal_entity_id'),
                  render: (a: ProviderAccount) => entityName(a.legalEntityId),
                },
              ]
            : []),
          {
            key: 'environment',
            header: t('delivery.providers.columns.environment'),
            render: (a) => t(`delivery.providers.env.${a.environment === 'production' ? 'production' : 'sandbox'}`),
          },
          {
            key: 'credentials',
            header: t('delivery.providers.columns.credentials'),
            render: (a) => {
              const needed = secretsNeeded(a);
              const missing = needed.filter((s) => s.required && !a.secretKeys.includes(s.key));
              if (needed.length === 0) return '—';
              return missing.length > 0 ? (
                <StatusTag status="failed">{t('delivery.providers.credentialsMissing')}</StatusTag>
              ) : (
                <StatusTag status="active">{t('delivery.providers.credentialsSet')}</StatusTag>
              );
            },
          },
          {
            key: 'health',
            header: t('providers.health.column'),
            render: (a) => {
              const h = healthOf(a.id);
              if (!h) return '—';
              return (
                <>
                  <StatusTag status={HEALTH_TONE[h.status] ?? 'inactive'}>
                    {t(`providers.health.status.${h.status}` as I18nKey, { defaultValue: h.status })}
                  </StatusTag>
                  {h.lastSuccessAt && (
                    <p className="pos-delivery__hint">{t('providers.health.lastSuccess', { at: format.dateTime(h.lastSuccessAt) })}</p>
                  )}
                </>
              );
            },
          },
          {
            key: 'isActive',
            header: t('delivery.providers.columns.status'),
            render: (a) => (
              <StatusTag status={a.isActive ? 'active' : 'inactive'}>
                {t(a.isActive ? 'delivery.providers.active' : 'delivery.providers.inactive')}
              </StatusTag>
            ),
          },
        ]}
        rowActions={
          canEdit
            ? (a) => <RowAction action={t('admin.delete')} record={a.name} icon={TrashCan} onClick={() => setDeleting(a)} />
            : undefined
        }
        empty={search ? undefined : { heading: t('admin.emptyTitle'), description: t(area.empty) }}
        pagination={pages.pagination}
      />

      {editing && providers.data && (
        <AccountDialog
          capability={capability}
          title={t(area.title)}
          legalEntities={byEntity ? (entities.data ?? []) : undefined}
          brands={byBrand ? (brands.data ?? []) : undefined}
          providers={providers.data}
          account={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: accountsKey })}
        />
      )}
      {deleting && (
        <Modal
          open
          danger
          preventCloseOnClickOutside
          modalHeading={t('delivery.providers.deleteTitle')}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('common.delete')}
          secondaryButtonText={t('common.cancel')}
          primaryButtonDisabled={remove.isPending}
          onRequestClose={() => setDeleting(undefined)}
          onRequestSubmit={() => remove.mutate(deleting)}
        >
          <p>{t('delivery.providers.deleteBody', { name: deleting.name })}</p>
          {remove.error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(remove.error))}
            />
          )}
        </Modal>
      )}
    </section>
  );
}
